<template>
  <main class="cb-page main-screen">
    <header class="main-screen__header">
      <h1 class="main-screen__title">Гляделки</h1>
      <StoragePill v-if="session.me" class="main-screen__pill" :count="session.stars" clickable @click="openProfile" />
      <span v-else class="main-screen__pill-skeleton cb-skeleton" aria-hidden="true"></span>
    </header>

    <ProfileRetryCard v-if="profileFailed" />
    <ProfileCard v-else class="main-screen__profile" :player="session.me" layout="summary" :avatar-size="44" editable clickable @open="openProfile" @edit="openProfile" />

    <nav class="main-screen__tiles" aria-label="Разделы">
      <GlassCard tag="button" type="button" class="main-tile" padding="none" interactive @click="openLeaderboard">
        <AppIcon name="trophy" :size="22" class="main-tile__icon main-tile__icon--trophy" />
        <span class="main-tile__label">Лидерборд</span>
        <AppIcon name="chevron-right" :size="16" class="main-tile__chevron" />
      </GlassCard>
      <GlassCard tag="button" type="button" class="main-tile" padding="none" interactive aria-label="Турнир — скоро" @click="openTournament">
        <AppIcon name="crown" :size="22" class="main-tile__icon main-tile__icon--crown" />
        <span class="main-tile__label">Турнир</span>
        <span class="main-tile__badge">Скоро</span>
        <AppIcon name="chevron-right" :size="16" class="main-tile__chevron main-tile__chevron--muted" />
      </GlassCard>
    </nav>

    <section class="main-hero" aria-labelledby="main-hero-title">
      <img v-for="hero in HERO_IMAGES" :key="hero.mode" class="main-hero__img" :class="{ 'main-hero__img--active': activeMode === hero.mode }" :src="hero.url" width="960" height="720" alt="" draggable="false" decoding="async" fetchpriority="high" />
      <span class="main-hero__fade" aria-hidden="true"></span>
      <OnlinePill class="main-hero__online" />
      <h2 id="main-hero-title" class="main-hero__title">{{ modeContent.heroTitle }}</h2>
      <button class="main-hero__toggle" type="button" :disabled="starting" :aria-label="activeMode === 'BLINK' ? 'Включить режим «Открытый рот»' : 'Включить режим «Моргание»'" @click="toggleMode"></button>
    </section>

    <section class="main-screen__mode" aria-labelledby="game-mode-title">
      <h2 id="game-mode-title">Режим игры</h2>
      <ModeSwitch class="main-screen__switch" :model-value="activeMode" :disabled="starting" @update:model-value="session.setMode" />
      <p class="main-screen__hint">{{ modeContent.hint }}</p>
    </section>

    <div class="main-screen__actions">
      <AppButton icon="play" size="md" class="main-screen__start" block :loading="starting" @click="play">Начать игру</AppButton>
      <AppButton variant="secondary" size="md" icon="users" block class="main-screen__friend" :disabled="starting" @click="openFriendRoom">Играть с другом</AppButton>
      <button class="main-screen__rules" type="button" @click="openRules">
        <AppIcon name="book-open" :size="20" class="main-screen__rules-icon" />
        <span>Правила игры</span>
      </button>
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import GlassCard from "@/components/ui/GlassCard.vue";
import ModeSwitch from "@/components/ui/ModeSwitch.vue";
import ProfileCard from "@/components/ui/ProfileCard.vue";
import StoragePill from "@/components/ui/StoragePill.vue";
import OnlinePill from "./main/OnlinePill.vue";
import ProfileRetryCard from "./main/ProfileRetryCard.vue";
import heroBlink from "@/assets/img/layout-pack/hero-blink.jpg";
import heroMouth from "@/assets/img/layout-pack/hero-open-mouth.jpg";
import { useSessionStore } from "@/stores/session";
import { useToast } from "@/composables/useToast";
import { acquireCamera, cameraErrorMessage, releaseCamera } from "@/services/media";
import realtime from "@/services/realtime";
import telegram from "@/services/telegram";

const HERO_IMAGES = Object.freeze([{ mode: "BLINK", url: heroBlink }, { mode: "MOUTH", url: heroMouth }]);
const MODE_CONTENT = Object.freeze({
  BLINK: { heroTitle: "Кто моргнёт первым?", hint: "Кто первым моргнёт — проиграл." },
  MOUTH: { heroTitle: "Кто закроет рот первым?", hint: "Кто первым закроет рот — проиграл." },
});
const router = useRouter();
const session = useSessionStore();
const toast = useToast();
const starting = ref(false);
const activeMode = computed(() => session.mode === "MOUTH" ? "MOUTH" : "BLINK");
const modeContent = computed(() => MODE_CONTENT[activeMode.value]);
const profileFailed = computed(() => !session.me && !!session.meError && !session.meLoading);
let unmounted = false;
let offReconnected = null;

function toggleMode() {
  if (starting.value) return;
  telegram.haptic("selection");
  session.setMode(activeMode.value === "BLINK" ? "MOUTH" : "BLINK");
}

function refreshMe() { session.loadMe().catch(() => null); }
function openProfile() { router.push("/profile"); }
function openLeaderboard() { router.push("/leaderboard"); }
function openRules() { router.push("/rules"); }
function openFriendRoom() {
  if (starting.value) return;
  telegram.haptic("medium");
  router.push("/room");
}
function openTournament() { telegram.haptic("light"); toast.info("Турниры скоро"); }

async function play() {
  if (starting.value) return;
  telegram.haptic("medium");
  if (!session.agreementAccepted) {
    router.push({ path: "/agreement", query: { next: "search" } });
    return;
  }
  starting.value = true;
  try {
    await acquireCamera();
    if (unmounted) releaseCamera();
    else router.push("/search");
  } catch (error) {
    if (!unmounted) toast.error(cameraErrorMessage(error));
  } finally { starting.value = false; }
}

onMounted(() => {
  refreshMe();
  offReconnected = realtime.on("reconnected", refreshMe);
});
onUnmounted(() => {
  unmounted = true;
  if (offReconnected) offReconnected();
});
</script>

<style scoped>
/* Content keeps its natural minimum: very short windows / larger text may scroll. */
.main-screen {
  max-width: 460px;
  height: var(--cb-vh);
  min-height: min-content;
  margin-inline: auto;
  gap: clamp(6px, calc(var(--cb-vh) * .012), 12px);
  padding-top: calc(var(--cb-safe-top) + 8px);
  padding-bottom: calc(var(--cb-safe-bottom) + 12px);
  --cb-text-2: #b6bab1;
}
.main-screen > * { flex-shrink: 0; }
.main-screen__header { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 48px; }
.main-screen__title { min-width: 0; font-size: clamp(26px, 8.1vw, 32px); font-weight: 800; letter-spacing: -.025em; line-height: 1.1; }
.main-screen__pill { flex-shrink: 0; min-width: 108px; min-height: 46px; padding: 8px 10px; gap: 8px; border-radius: 14px; box-shadow: none; }
.main-screen__pill :deep(.storage-pill__star) { width: 26px; height: 26px; filter: none; }
.main-screen__pill :deep(.storage-pill__label) { order: 1; font-size: 10px; }
.main-screen__pill :deep(.storage-pill__count) { order: 0; font-size: 20px; }
.main-screen__pill-skeleton { width: 108px; height: 46px; flex-shrink: 0; border-radius: 14px; }
.main-screen__profile { min-height: 64px; padding: 6px 10px; border-radius: 16px; box-shadow: none; }
.main-screen :deep(.profile-retry__btn) { min-height: 44px; }
.main-screen__tiles { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.main-screen__tiles .main-tile { display: flex; align-items: center; gap: 7px; min-height: 48px; padding: 8px 10px; margin: 0; border-radius: 14px; box-shadow: none; font: inherit; }
.main-tile__icon--trophy { color: var(--cb-gold); }
.main-tile__icon--crown { color: #e8df88; }
.main-tile__label { min-width: 0; flex: 1 1 auto; font-size: 13px; font-weight: 650; white-space: nowrap; }
.main-tile__chevron { color: var(--cb-lime); }
.main-tile__chevron--muted { color: var(--cb-text-2); }
.main-tile__badge { padding: 3px 6px; border-radius: 999px; background: rgba(255,255,255,.07); color: var(--cb-text-2); font-size: 10px; line-height: 1.2; }
.main-hero { position: relative; flex: 1 0 120px; min-height: 120px; max-height: 320px; width: 100%; border: 1px solid var(--cb-border-strong); border-radius: 18px; overflow: hidden; isolation: isolate; background: var(--cb-surface); }
.main-hero__toggle { position: absolute; inset: 0; z-index: 3; padding: 0; border: 0; border-radius: inherit; background: transparent; cursor: pointer; }
.main-hero__toggle:focus-visible { outline-offset: -4px; }
.main-hero__toggle:disabled { cursor: default; }
.main-hero__img { position: absolute; inset: 0; width: 100%; height: 100%; max-width: none; object-fit: cover; object-position: center; opacity: 0; transition: opacity 200ms ease; user-select: none; -webkit-user-drag: none; }
.main-hero__img--active { opacity: 1; }
.main-hero__fade { position: absolute; inset: 48% 0 0; z-index: 1; background: linear-gradient(transparent, rgba(5,8,4,.85)); pointer-events: none; }
.main-hero__title { position: absolute; z-index: 2; left: 12px; right: 12px; bottom: 14px; font-size: clamp(20px, 5.9vw, 25px); font-weight: 750; line-height: 1.15; letter-spacing: -.025em; text-align: center; color: #fff; text-shadow: 0 2px 10px rgba(0,0,0,.65); }
.main-hero__online { position: absolute; z-index: 2; top: 10px; right: 10px; margin: 0; height: 25px; padding: 0 7px; gap: 5px; border-color: transparent; box-shadow: none; font-size: 11px; }
.main-hero__online :deep(.online-pill__count) { color: var(--cb-text); font-weight: 500; }
.main-hero__online :deep(.online-pill__dot) { width: 7px; height: 7px; box-shadow: none; }
.main-hero__online :deep(.online-pill__dot::after) { display: none; }
.main-screen__mode { display: grid; gap: 6px; margin-top: auto; }
.main-screen__mode h2 { font-size: 17px; line-height: 1.2; font-weight: 700; }
.main-screen__switch { padding: 3px; border-radius: 14px; box-shadow: none; }
.main-screen__switch :deep(.mode-switch__option) { height: 44px; font-size: 13px; padding-inline: 7px; gap: 7px; }
.main-screen__switch :deep(.mode-switch__thumb) { top: 3px; bottom: 3px; left: 3px; width: calc(50% - 3px); border-radius: 11px; box-shadow: 0 0 8px rgba(221,245,90,.12); }
.main-screen__hint { min-height: 18px; font-size: 12px; line-height: 1.45; text-align: center; color: var(--cb-text-2); }
.main-screen__actions { display: grid; gap: 6px; }
.main-screen__actions .app-button { height: 48px; border-radius: 14px; font-size: 16px; gap: 9px; }
.main-screen__actions .main-screen__start { box-shadow: 0 0 14px rgba(221,245,90,.14); font-weight: 750; }
.main-screen__actions .main-screen__friend { height: 44px; background: #181c17; border-color: rgba(255,255,255,.15); box-shadow: none; font-weight: 550; }
.main-screen__rules {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid var(--cb-lime-border);
  border-radius: 14px;
  background: var(--cb-lime-softer);
  color: var(--cb-lime);
  font-size: 14px;
  font-weight: 650;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
}
.main-screen__rules:active { background: var(--cb-lime-soft); }
.main-screen__rules-icon { flex-shrink: 0; }
@media (max-width: 350px) {
  .main-screen__tiles .main-tile { gap: 5px; padding-inline: 7px; }
  .main-tile__label { font-size: 11px; }
  .main-tile__badge { padding-inline: 4px; font-size: 9px; }
  .main-tile__chevron { width: 13px; }
  .main-screen__switch :deep(.mode-switch__option) { font-size: 12px; gap: 5px; }
}
@media (prefers-reduced-motion: reduce) {
  .main-hero__img, .main-screen__switch :deep(.mode-switch__thumb) { transition: none; }
}
</style>